<template>
  <div class="fts-automation-design-modal">
    <!-- automation modal -->
    <fts-design-modal
      width="1200px"
      :backdrop="false"
      class="fts-event-config-modal"
      @opened="afterShow"
      ref="ftsDesignModal"
    >
      <template #title>
        <!-- event type -->
        <div class="event-type">
          <!-- event type: vector -->
          <span v-if="eventType === 'vector'">
            When module is changed
          </span>
          <!-- event type: information console -->
          <span v-if="eventType === 'informationConsole'">
            When information is changed
          </span>
          <!-- event type: activity console -->
          <span v-if="eventType === 'activityConsole'">
            When activity is performed
          </span>
          <!-- event type: standard console -->
          <span v-if="eventType === 'standardConsole'">
            When standard activity is performed
          </span>
          <!-- event type: state console -->
          <span v-if="eventType === 'stateConsole'">
            When state is changed
          </span>
          <!-- event type:activity -->
          <template v-else-if="eventType === 'activity'">
            <span class="event-type-label">
              When performing
            </span>
            <span class="fts-inline-spacer-5" />
            <div class="event-type-activity">
              <fts-moji
                class="event-type-emoji"
                :emoji="getActivity.emoji"
                :size="20"
                v-if="getActivity && getActivity.emoji"
              />
              <span class="event-type-label-ellipsis">
                {{ (getActivity && getActivity.name) || "(undefined)" }}
              </span>
            </div>
          </template>
          <!-- event type:standardActivity -->
          <template v-else-if="eventType === 'standardActivity'">
            <span>
              When performing
            </span>
            <span class="fts-inline-spacer-5" />
            <!-- <div class="event-type-activity"> -->
            <span class="event-type-label-ellipsis">
              {{
                (getStandardActivity && getStandardActivity.name) ||
                "(undefined)"
              }}
            </span>
            <!-- </div> -->
          </template>
          <!-- event type:information -->
          <template v-else-if="eventType === 'information'">
            <span>
              When
            </span>
            <span class="fts-inline-spacer-5" />
            <div class="event-type-information">
              <i
                class="event-type-icon"
                :class="informationIcon(getInformation.type)"
                v-if="getInformation"
              />
              <span class="event-type-label-ellipsis">
                {{
                  (getInformation && getInformation.displayName) ||
                  "(undefined)"
                }}
              </span>
            </div>
            <span class="fts-inline-spacer-5" />
            <span>
              changed
            </span>
          </template>
          <!-- event type:state -->
          <template v-else-if="eventType === 'state'">
            <span>
              When state has
            </span>
            <span class="fts-inline-spacer-5" />
          </template>
          <!-- event type:due (information date, time) -->
          <template v-else-if="eventType === 'due'">
            <span>
              When
            </span>
            <span class="fts-inline-spacer-5" />
            <div class="event-type-information">
              <i
                class="event-type-icon"
                :class="informationIcon(getInformation && getInformation.type)"
              />
              <span class="event-type-label-ellipsis">
                {{
                  (getInformation && getInformation.displayName) ||
                  "(undefined)"
                }}
              </span>
            </div>
            <span class="fts-inline-spacer-5" />
            <span>
              is
            </span>
            <span class="fts-inline-spacer-5" />
          </template>
          <!-- event type:state / due -->
          <template v-if="eventType === 'state' || eventType === 'due'">
            <fts-dropdown-select
              append-to-body
              append-toggle-class="event-type-select__toggle"
              append-content-class="event-type-content event-type-dropdown-menu"
              v-model="formData.trigger"
              :options="triggers"
              :reduce="(x) => x.id"
              class="event-type-select"
            >
              <template #toggle="{ selectedOptionValue }">
                {{ selectedOptionValue }}
                <font-awesome-icon
                  :icon="['fas', 'angle-down']"
                  class="event-type-toggle-icon"
                />
              </template>
            </fts-dropdown-select>
            <span class="fts-inline-spacer-5" />
          </template>
          <!-- event type:state -->
          <template v-if="eventType === 'state'">
            <span class="fts-inline-spacer-5" />
            <span>
              <fts-state :state="getState" class="event-type-state" />
            </span>
            <span class="fts-inline-spacer-5" />
          </template>
        </div>
      </template>
      <!-- description -->
      <template #description>
        <!-- notification -->
        <template v-if="formData.actionType === 'notification'">
          <span class="design-modal-header-tag">Notification</span>
          <span class="design-modal-header-description">
            send a notification, reminder or alert
          </span>
        </template>
        <!-- advanced -->
        <template v-if="formData.actionType === 'advanced'">
          <span class="design-modal-header-tag">Advanced</span>
          <span class="design-modal-header-description">
            advanced script (beta)
          </span>
        </template>
        <!-- automation -->
        <template v-if="formData.actionType === 'automation'">
          <span class="design-modal-header-tag">Automation</span>
          <span class="design-modal-header-description">
            automation blocks
          </span>
        </template>

        <!-- event badge -->
        <div class="event-badges" v-if="eventBadges">
          <fts-tippy
            v-for="x in eventBadges"
            :key="`event-badge-${x.name}`"
            :content="x.description"
          >
            <div
              class="fts-badge badge-sm badge-default badge-pill"
              slot="trigger"
            >
              {{ x.name }}
            </div>
          </fts-tippy>
        </div>
      </template>
      <!-- actions -->
      <template #actions>
        <fts-toggle-switch
          :formData="formData"
          :item="{ fieldName: 'enabled' }"
          mat-class
          :size="15"
          label-dir="left"
          class="designer-header-action"
        >
          <div slot="label" class="event-toggle-label">Active</div>
        </fts-toggle-switch>
      </template>
      <!-- body -->
      <template #body>
        <!-- description -->
        <div class="fts-form-group">
          <fts-contenteditable
            v-model="formData.description"
            text-wrap
            placeholder="enter description (optional)"
          />
        </div>
        <!-- notification -->
        <div
          class="fts-row gutters"
          v-if="
            formData.actionType === 'notification' ||
            (isInternal && formData.channel && formData.actionType !== 'notification')
          "
        >
          <div class="fts-col fts-col-md-4">
            <fts-form-element
              :form-data="formData"
              :item="{
                fieldName: 'channel',
                icon: 'fts-icon-menu-caret-down',
              }"
              comfort-design
            >
              <fts-toggle-tokens
                slot="formElement"
                slot-scope="{ formData, item }"
                v-model="formData[item.fieldName]"
                :options="channels"
                comfort-design
                class="event-channel-container"
                @selected="preview"
              >
                <template #optionLabel="{option, optionLabel}">
                  <font-awesome-icon
                    :icon="option.icon"
                    class="event-channel-icon"
                    v-if="option.icon"
                  />
                  {{optionLabel}}
                  <template v-if="option.beta">
                    <span class="fts-inline-spacer-1"></span>
                    <fts-release-badge class="fts-float-right" />
                  </template>
                  <template v-if="option.internal">
                    <span class="fts-inline-spacer-1"></span>
                    <fts-release-badge badge-label="Internal" class="fts-float-right" />
                  </template>
                </template>
              </fts-toggle-tokens>
            </fts-form-element>
          </div>
          <!-- to -->
          <div class="fts-col fts-col-md-8">
            <fts-form-element
              :form-data="formData"
              :item="{
                label: 'To',
                fieldName: 'recipients',
                icon: 'fts-icon-menu-caret-down',
              }"
              comfort-design
            >
              <fts-select
                slot="formElement"
                slot-scope="{ formData, item }"
                :formData="formData"
                :item="item"
                :filterable="false"
                :options="recipientOptions"
                :reduce="(x) => x"
                multiple
                @search="fetchRecipients"
                @search:focus="preloadRecipients"
                comfort-design
              >
                <template #selected-option-container="{ option }">
                  <!-- member -->
                  <fts-profile
                    :user-name="option.identity"
                    profile-name-class="fts-identity"
                    v-if="option.recipientType === 0"
                  >
                    <template #profileName="{ userProfileUsername }">
                      <div class="identity__name">{{ option.name }}</div>
                      <div class="identity__tag">
                        {{ userProfileUsername }}
                      </div>
                      <div class="identity__badge">User</div>
                    </template>
                  </fts-profile>
                  <!-- user profile -->
                  <div
                    class="fts-identity"
                    v-else-if="option.recipientType === 7"
                  >
                    <fts-moji
                      :emoji="getModuleEmoji(option.id)"
                      :size="18"
                      class="identity__emoji"
                      v-if="getModuleEmoji(option.id)"
                    />
                    <div class="identity__name">
                      {{ getModuleName(option.id, option.name) }}
                    </div>
                    <div class="identity__badge">Profile</div>
                  </div>
                  <!-- system -->
                  <div class="fts-identity" v-else>
                    <i
                      :class="
                        informationIcon(option.informationType || option.type)
                      "
                      class="identity__icon identity__icon--information"
                      v-if="
                        option.informationType != null || option.type != null
                      "
                    />
                    <div class="identity__name">{{ option.name }}</div>
                    <div class="identity__badge">Information</div>
                  </div>
                </template>
                <template #option="{ option }">
                  <!-- member -->
                  <fts-profile
                    :user-name="option.identity"
                    profile-name-class="fts-identity"
                    v-if="option.recipientType === 0"
                  >
                    <template #profileName="{ userProfileUsername }">
                      <div class="identity__name">{{ option.name }}</div>
                      <div class="identity__tag">
                        {{ userProfileUsername }}
                      </div>
                      <div class="identity__badge">User</div>
                    </template>
                  </fts-profile>
                  <!-- user profile -->
                  <div
                    class="fts-identity"
                    v-else-if="option.recipientType === 7"
                  >
                    <fts-moji
                      :emoji="getModuleEmoji(option.id)"
                      :size="18"
                      class="identity__emoji"
                      v-if="getModuleEmoji(option.id)"
                    />
                    <div class="identity__name">{{ option.name }}</div>
                    <div class="identity__badge">Profile</div>
                  </div>
                  <!-- system -->
                  <div class="fts-identity" v-else>
                    <i
                      :class="
                        informationIcon(option.informationType || option.type)
                      "
                      class="identity__icon identity__icon--information"
                      v-if="
                        option.informationType != null || option.type != null
                      "
                    />
                    <div class="identity__name">{{ option.name }}</div>
                    <div class="identity__badge">Information</div>
                  </div>
                </template>
              </fts-select>
            </fts-form-element>
          </div>
          <!-- message -->
          <div class="fts-col fts-col-md-12">
            <!-- Message (Optional) -->
            <!-- customize message -->
            <div
              class="fts-col design-modal-form-group"
              v-if="formData.actionType === 'notification'"
            >
              <fts-toggle-switch
                :form-data="formData"
                :item="{ fieldName: 'message' }"
                mat-class
                :size="15"
                class="design-modal-toggle-switch"
              >
                <div slot="label" class="design-modal-toggle-switch__label">
                  Message (Optional)
                </div>
              </fts-toggle-switch>
              <fts-transition-expand>
                <div
                  class="design-modal-card design-modal-card-mt"
                  v-if="formData.message"
                >
                  <div class="fts-row gutters">
                    <!-- subject -->
                    <div
                      class="fts-col"
                      v-if="
                        formData.channel != 'whatsapp' &&
                        formData.channel != 'sms'
                      "
                    >
                      <fts-form-element
                        :form-data="formData"
                        :item="{
                          type: 32,
                          displayName: 'Subject',
                          fieldName: 'subjectMessage',
                        }"
                        comfort-design
                      >
                        <fts-formula
                          slot="formElement"
                          slot-scope="{ formData, item }"
                          :variables="variables"
                          v-model="formData[item.fieldName]"
                        />
                      </fts-form-element>
                    </div>
                    <!-- message -->
                    <div class="fts-col">
                      <fts-form-element
                        :form-data="formData"
                        :item="{
                          type: 32,
                          displayName: 'Message',
                          fieldName: 'messageTemplate',
                        }"
                        comfort-design
                      >
                        <fts-formula
                          slot="formElement"
                          :template="true"
                          slot-scope="{ formData, item }"
                          :variables="variables"
                          v-model="formData[item.fieldName]"
                        />
                      </fts-form-element>
                    </div>
                    <!-- email attachment -->
                    <div
                      class="fts-col fts-col-md-4 design-modal-form-group"
                      v-if="formData.channel === 'email'"
                    >
                      <div class="design-modal-form-label">Attachments</div>
                      <div
                        class="event-attachment"
                        v-for="attachment in formData.attachments"
                        :key="attachment.id"
                      >
                        <!-- attachment:select -->
                        <fts-select
                          :form-data="attachment"
                          :item="{ fieldName: 'templateId' }"
                          :options="entryTemplates"
                          comfort-design
                          class="event-attachment-selection"
                        />
                        <!-- attachment:cog -->
                        <fts-btn
                          icon
                          text
                          @click="openAttachmentModal(attachment)"
                        >
                          <font-awesome-icon :icon="['fas', 'cog']" />
                        </fts-btn>
                        <!-- attachment:remove -->
                        <fts-btn
                          icon
                          text
                          @click="removeAttachment(attachment)"
                        >
                          <font-awesome-icon :icon="['far', 'trash-alt']" />
                        </fts-btn>
                      </div>
                      <!-- attachment:add -->
                      <fts-btn
                        sm
                        outline
                        color="primary"
                        text-transform-init
                        @click="addAttachment"
                      >
                        <font-awesome-icon :icon="['fas', 'plus']" />Add
                        Attachment
                      </fts-btn>
                    </div>
                  </div>
                </div>
              </fts-transition-expand>
            </div>
          </div>
        </div>
        <!-- test -->
        <div
          class="event-test design-modal-form-group"
          v-if="
            formData.actionType === 'notification' ||
            formData.actionType === 'advanced'
          "
        >
          <div class="event-test-legend">
            Test
            {{
              formData.actionType === "notification"
                ? "notification"
                : "automation"
            }}
            with:
          </div>
          <div class="fts-row gutters">
            <!-- entry -->
            <div class="fts-col fts-col-md-4">
              <fts-form-element
                :form-data="formData"
                :item="{
                  displayName: 'Entry',
                  key: 'testLocusId',
                  fieldName: 'testLocus',
                  connectionId: vectorId,
                  type: 7,
                }"
                comfort-design
                class="event-test-selector"
              >
                <fts-dropdown
                  slot="formElement"
                  slot-scope="{ formData, item }"
                  :form-data="formData"
                  :item="item"
                  :module-design="vectorId.toString()"
                  comfort-design
                />
              </fts-form-element>
            </div>
            <!-- activity -->
            <div class="fts-col fts-col-md-4" v-if="formData.testLocusId">
              <fts-form-element
                :form-data="formData"
                :item="{
                  displayName: 'Activity',
                  key: 'testLocusDetailId',
                  fieldName: 'testLocusDetail',
                  connectionId: vectorId,
                  type: 7,
                }"
                comfort-design
                class="event-test-selector"
              >
                <fts-dropdown
                  slot="formElement"
                  slot-scope="{ formData, item }"
                  :form-data="formData"
                  :item="item"
                  :module-design="vectorId.toString()"
                  :locus-id="formData.testLocusId"
                  locus-detail
                  comfort-design
                />
              </fts-form-element>
            </div>
            <!-- action -->
            <div class="fts-col">
              <div class="event-test-action">
                <fts-btn
                  sm
                  outline
                  text-transform-init
                  :loading="previewLoading"
                  @click="preview"
                >
                  Preview
                </fts-btn>
                <fts-btn
                  sm
                  outline
                  text-transform-init
                  :loading="previewSending"
                  @click="testAutomation"
                >
                  Test Send
                </fts-btn>
              </div>
              <!-- action helper -->
              <div class="event-test-action-helper">
                By clicking <b>Test Send</b> only you will receive this notification.
              </div>
            </div>
          </div>
          <!-- preview -->
          <div class="fts-col event-preview" v-if="notificationPreview">
            <div class="event-preview-result">
              <div class="event-preview-wrapper">
                <!-- subject -->
                <div class="event-preview-subject-wrapper">
                  <div class="fts-badge badge-default badge-sm event-preview-subject-badge">
                    Subject
                  </div>
                  <span class="event-preview-subject">
                    {{ notificationPreview.subject }}
                  </span>
                </div>
                <!-- recipient -->
                <div
                  class="event-preview-recipient-container"
                  v-if="
                    notificationPreview &&
                    notificationPreview.recipients &&
                    notificationPreview.recipients.length
                  "
                >
                  <div>To:</div>
                  <template v-for="recipient in notificationPreview.recipients">
                    <fts-profile
                      :key="recipient"
                      :user-name="recipient"
                      profile-name-class="fts-identity"
                      class="event-preview-recipient"
                    >
                      <template #profileName="{ userProfileName }">
                        <fts-tippy :content="recipient" class="event-preview-recipient-tippy">
                          <div slot="trigger" class="identity__name">{{ userProfileName ||  recipient}}</div>
                        </fts-tippy>
                      </template>
                    </fts-profile>
                  </template>
                </div>
              </div>
              <!-- content -->
              <div class="event-preview-content">
                <iframe
                  class="event-preview-iframe"
                  ref="notificationContentIframe"
                />
              </div>
            </div>
          </div>
        </div>
        <!-- automation -->
        <div
          class="design-modal-form-group"
          v-if="formData.actionType === 'automation' && beta"
        >
          <div class="event-automation-selection">
            <div
              class="automation-design-toggle"
              :class="{
                'automation-design-toggle--active': automationType === 'before',
              }"
              @click.self="automationType = 'before'"
            >
              Before executing
              <fts-btn
                text
                icon
                class="automation-design-toggle__action"
                @click="
                  formData.automationBefore = null;
                  automationType = null;
                "
                v-if="Boolean(formData.automationBefore)"
              >
                <font-awesome-icon :icon="['far', 'trash-alt']" />
              </fts-btn>
            </div>
            <div
              class="automation-design-toggle"
              :class="{
                'automation-design-toggle--active': automationType === 'after',
              }"
              @click.self="automationType = 'after'"
            >
              After executing
              <fts-btn
                text
                icon
                class="automation-design-toggle__action"
                @click="
                  formData.automation = null;
                  automationType = null;
                "
                v-if="Boolean(formData.automation)"
              >
                <font-awesome-icon :icon="['far', 'trash-alt']" />
              </fts-btn>
            </div>
            <fts-release-badge class="fts-align-top" />
          </div>
          <fts-automation-designer
            :moduleId="moduleId"
            :data="formData.automation"
            :key="automationType"
            v-if="automationType == 'after'"
          />
          <fts-automation-designer
            :moduleId="moduleId"
            :data="formData.automationBefore"
            :key="automationType"
            v-else-if="automationType == 'before'"
          />
        </div>
        <!-- action type -->
        <div class="fts-row gutters" v-if="formData.actionType !== 'notification' && beta">
          <div class="fts-col fts-col-md-4 design-modal-form-group"> 
            <fts-toggle-switch
              v-model="actionTypeAdvanced"
              mat-class
              :size="15"
              class="design-modal-toggle-switch"
            >
              <div slot="label" class="design-modal-toggle-switch__label">
                Enable Advanced&nbsp;
                <fts-release-badge class="fts-float-right" />
              </div>
            </fts-toggle-switch>
          </div>
        </div>
        <!-- advanced -->
        <div v-if="formData.actionType === 'advanced' && beta">
          <!-- before script -->
          <div class="design-modal-form-group">
            <fts-release-badge />
            <fts-script-editor
              editor-id="beforeScript"
              v-model="formData.beforeScript"
              lang="python"
              label="Trigger Before Script"
            />
          </div>
          <!-- after script -->
          <div class="design-modal-form-group">
            <fts-release-badge />
            <fts-script-editor
              editor-id="afterScript"
              v-model="formData.afterScript"
              lang="python"
              label="Trigger After Script"
            />
          </div>
        </div>
        <!-- beta message (automation and advanced only) -->
        <div
          class="event-beta-message fts-text-center"
          v-if="
            (formData.actionType === 'automation' ||
              formData.actionType === 'advanced') &&
            !beta
          "
        >
          <div class="event-beta-message-title">Coming Soon. Stay Tuned.</div>
          <div class="event-beta-message-description">
            To get more information please visit our
            <a
              class="fts-link-primary"
              :href="websiteURL"
              target="_blank"
            >
              website
            </a>
            and
            <a
              class="fts-link-primary"
              :href="communityURL"
              target="_blank"
            >
              community
            </a>.
          </div>
        </div>
        <!-- advanced settings -->
        <fts-collapsible-element v-model="advanced">
          <fts-btn
            slot="toggle"
            slot-scope="{ isVisible, toggle }"
            text
            text-transform-init
            :class="{ open: isVisible }"
            class="design-modal-advanced-toggle"
            @click="toggle()"
          >
            Advanced settings
            <font-awesome-icon
              :icon="['fas', 'caret-right']"
              class="design-modal-advanced-toggle-icon"
            />
          </fts-btn>
          <div
            slot="content"
            class="design-modal-advanced-content fts-row gutters"
          >
            <!-- cc -->
            <div class="fts-col" v-if="formData.channel == 'email'">
              <fts-form-element
                :form-data="formData"
                :item="{
                  label: 'CC',
                  fieldName: 'cc',
                  icon: 'fts-icon-menu-caret-down',
                }"
                comfort-design
              >
                <fts-select
                  slot="formElement"
                  slot-scope="{ formData, item }"
                  :formData="formData"
                  :item="item"
                  :filterable="false"
                  :options="recipientOptions"
                  :reduce="(x) => x"
                  multiple
                  @search="fetchRecipients"
                  @search:focus="preloadRecipients"
                  comfort-design
                >
                  <template #selected-option-container="{ option }">
                    <!-- member -->
                    <fts-profile
                      :user-name="option.identity"
                      profile-name-class="fts-identity"
                      v-if="option.recipientType === 0"
                    >
                      <template #profileName="{ userProfileUsername }">
                        <div class="identity__name">{{ option.name }}</div>
                        <div class="identity__tag">
                          {{ userProfileUsername }}
                        </div>
                        <div class="identity__badge">User</div>
                      </template>
                    </fts-profile>
                    <!-- user profile -->
                    <div
                      class="fts-identity"
                      v-else-if="option.recipientType === 7"
                    >
                      <fts-moji
                        :emoji="getModuleEmoji(option.id)"
                        :size="18"
                        class="identity__emoji"
                        v-if="getModuleEmoji(option.id)"
                      />
                      <div class="identity__name">
                        {{ getModuleName(option.id, option.name) }}
                      </div>
                      <div class="identity__badge">Profile</div>
                    </div>
                    <!-- system -->
                    <div class="fts-identity" v-else>
                      <i
                        :class="
                          informationIcon(option.informationType || option.type)
                        "
                        class="identity__icon identity__icon--information"
                        v-if="
                          option.informationType != null || option.type != null
                        "
                      />
                      <div class="identity__name">{{ option.name }}</div>
                      <div class="identity__badge">Information</div>
                    </div>
                  </template>
                  <template #option="{ option }">
                    <!-- member -->
                    <fts-profile
                      :user-name="option.identity"
                      profile-name-class="fts-identity"
                      v-if="option.recipientType === 0"
                    >
                      <template #profileName="{ userProfileUsername }">
                        <div class="identity__name">{{ option.name }}</div>
                        <div class="identity__tag">
                          {{ userProfileUsername }}
                        </div>
                        <div class="identity__badge">User</div>
                      </template>
                    </fts-profile>
                    <!-- user profile -->
                    <div
                      class="fts-identity"
                      v-else-if="option.recipientType === 7"
                    >
                      <fts-moji
                        :emoji="getModuleEmoji(option.id)"
                        :size="18"
                        class="identity__emoji"
                        v-if="getModuleEmoji(option.id)"
                      />
                      <div class="identity__name">{{ option.name }}</div>
                      <div class="identity__badge">Profile</div>
                    </div>
                    <!-- system -->
                    <div class="fts-identity" v-else>
                      <i
                        :class="
                          informationIcon(option.informationType || option.type)
                        "
                        class="identity__icon identity__icon--information"
                        v-if="
                          option.informationType != null || option.type != null
                        "
                      />
                      <div class="identity__name">{{ option.name }}</div>
                      <div class="identity__badge">Information</div>
                    </div>
                  </template>
                </fts-select>
              </fts-form-element>
            </div>
            <!-- reply to -->
            <div class="fts-col" v-if="formData.channel == 'email'">
              <fts-form-element
                :form-data="formData"
                :item="{
                  label: 'Reply To',
                  fieldName: 'replyTo',
                  icon: 'fts-icon-menu-caret-down',
                }"
                comfort-design
              >
                <fts-select
                  slot="formElement"
                  slot-scope="{ formData, item }"
                  :formData="formData"
                  :item="item"
                  :filterable="false"
                  :options="recipientOptions"
                  :reduce="(x) => x"
                  multiple
                  @search="fetchRecipients"
                  @search:focus="preloadRecipients"
                  comfort-design
                >
                  <template #selected-option-container="{ option }">
                    <!-- member -->
                    <fts-profile
                      :user-name="option.identity"
                      profile-name-class="fts-identity"
                      v-if="option.recipientType === 0"
                    >
                      <template #profileName="{ userProfileUsername }">
                        <div class="identity__name">{{ option.name }}</div>
                        <div class="identity__tag">
                          {{ userProfileUsername }}
                        </div>
                        <div class="identity__badge">User</div>
                      </template>
                    </fts-profile>
                    <!-- user profile -->
                    <div
                      class="fts-identity"
                      v-else-if="option.recipientType === 7"
                    >
                      <fts-moji
                        :emoji="getModuleEmoji(option.id)"
                        :size="18"
                        class="identity__emoji"
                        v-if="getModuleEmoji(option.id)"
                      />
                      <div class="identity__name">
                        {{ getModuleName(option.id, option.name) }}
                      </div>
                      <div class="identity__badge">Profile</div>
                    </div>
                    <!-- system -->
                    <div class="fts-identity" v-else>
                      <i
                        :class="
                          informationIcon(option.informationType || option.type)
                        "
                        class="identity__icon identity__icon--information"
                        v-if="
                          option.informationType != null || option.type != null
                        "
                      />
                      <div class="identity__name">{{ option.name }}</div>
                      <div class="identity__badge">Information</div>
                    </div>
                  </template>
                  <template #option="{ option }">
                    <!-- member -->
                    <fts-profile
                      :user-name="option.identity"
                      profile-name-class="fts-identity"
                      v-if="option.recipientType === 0"
                    >
                      <template #profileName="{ userProfileUsername }">
                        <div class="identity__name">{{ option.name }}</div>
                        <div class="identity__tag">
                          {{ userProfileUsername }}
                        </div>
                        <div class="identity__badge">User</div>
                      </template>
                    </fts-profile>
                    <!-- user profile -->
                    <div
                      class="fts-identity"
                      v-else-if="option.recipientType === 7"
                    >
                      <fts-moji
                        :emoji="getModuleEmoji(option.id)"
                        :size="18"
                        class="identity__emoji"
                        v-if="getModuleEmoji(option.id)"
                      />
                      <div class="identity__name">{{ option.name }}</div>
                      <div class="identity__badge">Profile</div>
                    </div>
                    <!-- system -->
                    <div class="fts-identity" v-else>
                      <i
                        :class="
                          informationIcon(option.informationType || option.type)
                        "
                        class="identity__icon identity__icon--information"
                        v-if="
                          option.informationType != null || option.type != null
                        "
                      />
                      <div class="identity__name">{{ option.name }}</div>
                      <div class="identity__badge">Information</div>
                    </div>
                  </template>
                </fts-select>
              </fts-form-element>
            </div>
            <!-- recipient script -->
            <div class="fts-col design-modal-form-group" v-if="beta">
              <fts-toggle-switch
                :form-data="formData"
                :item="{ fieldName: 'enableRecipientScript' }"
                mat-class
                :size="15"
                class="design-modal-toggle-switch"
              >
                <div slot="label" class="design-modal-toggle-switch__label">
                  Recipient Script&nbsp;
                  <fts-release-badge class="fts-float-right" />
                </div>
              </fts-toggle-switch>
              <fts-transition-expand>
                <div
                  class="
                    design-modal-card design-modal-card-mt design-modal-card-pb
                  "
                  v-if="formData.enableRecipientScript"
                >
                  <fts-script-editor
                    editor-id="recipientScript"
                    v-model="formData.recipientScript"
                    lang="python"
                    label="Recipient Script"
                  />
                </div>
              </fts-transition-expand>
            </div>
            <!-- customize message -->
            <div
              class="fts-col design-modal-form-group"
              v-if="formData.actionType === 'notification'"
            >
              <fts-toggle-switch
                :form-data="formData"
                :item="{ fieldName: 'customizeMessage' }"
                mat-class
                :size="15"
                class="design-modal-toggle-switch"
              >
                <div slot="label" class="design-modal-toggle-switch__label">
                  Customize Message
                </div>
              </fts-toggle-switch>
              <fts-transition-expand>
                <div
                  class="design-modal-card design-modal-card-mt"
                  v-if="formData.customizeMessage"
                >
                  <div class="fts-row gutters">
                    <!-- subject -->
                    <div
                      class="fts-col"
                      v-if="
                        formData.channel != 'whatsapp' &&
                        formData.channel != 'sms'
                      "
                    >
                      <fts-form-element
                        :form-data="formData"
                        :item="{
                          type: 32,
                          displayName: 'Subject',
                          fieldName: 'subjectTemplate',
                        }"
                        comfort-design
                      >
                        <fts-template-editor
                          slot="formElement"
                          slot-scope="{ formData, item }"
                          :type="'text'"
                          :information="information"
                          v-model="formData[item.fieldName]"
                        />
                      </fts-form-element>
                    </div>
                    <!-- content -->
                    <div class="fts-col">
                      <fts-form-element
                        :form-data="formData"
                        :item="{
                          type: 32,
                          displayName: 'Content',
                          fieldName: 'template',
                        }"
                        comfort-design
                      >
                        <fts-template-editor
                          slot="formElement"
                          slot-scope="{ formData, item }"
                          :type="'text'"
                          :information="information"
                          v-model="formData[item.fieldName]"
                        />
                      </fts-form-element>
                    </div>
                    <!-- email attachment -->
                    <div
                      class="fts-col fts-col-md-4 design-modal-form-group"
                      v-if="formData.channel === 'email'"
                    >
                      <div class="design-modal-form-label">Attachments</div>
                      <div
                        class="event-attachment"
                        v-for="attachment in formData.attachments"
                        :key="attachment.id"
                      >
                        <!-- attachment:select -->
                        <fts-select
                          :form-data="attachment"
                          :item="{ fieldName: 'templateId' }"
                          :options="entryTemplates"
                          comfort-design
                          class="event-attachment-selection"
                        />
                        <!-- attachment:cog -->
                        <fts-btn
                          icon
                          text
                          @click="openAttachmentModal(attachment)"
                        >
                          <font-awesome-icon :icon="['fas', 'cog']" />
                        </fts-btn>
                        <!-- attachment:remove -->
                        <fts-btn
                          icon
                          text
                          @click="removeAttachment(attachment)"
                        >
                          <font-awesome-icon :icon="['far', 'trash-alt']" />
                        </fts-btn>
                      </div>
                      <!-- attachment:add -->
                      <fts-btn
                        sm
                        outline
                        color="primary"
                        text-transform-init
                        @click="addAttachment"
                      >
                        <font-awesome-icon :icon="['fas', 'plus']" />Add
                        Attachment
                      </fts-btn>
                    </div>
                    <!-- template -->
                    <div class="fts-col design-modal-form-group" v-if="beta">
                      <fts-release-badge />
                      <fts-script-editor
                        editor-id="messageTemplate"
                        v-model="formData.template"
                        lang="python"
                        label="Message Template"
                      />
                    </div>
                  </div>
                </div>
              </fts-transition-expand>
            </div>
            <!-- general -->
            <div class="fts-col design-modal-form-group">
              <fts-toggle-switch
                :form-data="formData"
                :item="{ fieldName: 'general' }"
                mat-class
                :size="15"
                class="design-modal-toggle-switch"
              >
                <div slot="label" class="design-modal-toggle-switch__label">
                  Execute exactly one time for each trigger without iterate
                  through entries
                </div>
              </fts-toggle-switch>
            </div>
            <!-- condition -->
            <div class="fts-col design-modal-form-group">
              <fts-toggle-switch
                :form-data="formData"
                :item="{ fieldName: 'condition' }"
                mat-class
                :size="15"
                class="design-modal-toggle-switch"
              >
                <div slot="label" class="design-modal-toggle-switch__label">
                  Condition
                </div>
              </fts-toggle-switch>
              <fts-transition-expand>
                <div
                  class="design-modal-card design-modal-card-mt"
                  v-if="formData.condition"
                >
                  <div class="fts-row gutters">
                    <!-- condition state -->
                    <div class="fts-col fts-col-md-4">
                      <fts-form-element
                        :form-data="formData"
                        :item="{
                          fieldName: 'conditionState',
                          icon: 'fts-icon-menu-caret-down',
                        }"
                        comfort-design
                      >
                        <fts-select
                          slot="formElement"
                          slot-scope="{ formData, item }"
                          :formData="formData"
                          :item="item"
                          :options="states"
                          comfort-design
                        >
                          <fts-state
                            slot="selected-option-container"
                            slot-scope="{ option }"
                            :state="option"
                          />
                          <fts-state
                            slot="option"
                            slot-scope="{ option }"
                            :state="option"
                          />
                        </fts-select>
                      </fts-form-element>
                    </div>
                    <!-- condition script -->
                    <div class="fts-col design-modal-form-group" v-if="beta">
                      <fts-collapsible-element
                        :condition="formData.conditionScript"
                        label="condition script"
                        immersive
                        beta
                      >
                        <fts-script-editor
                          slot="content"
                          editor-id="conditionScript"
                          v-model="formData.conditionScript"
                          lang="python"
                          label="Condition Script"
                        />
                      </fts-collapsible-element>
                    </div>
                  </div>
                </div>
              </fts-transition-expand>
            </div>
            <!-- timing -->
            <div class="fts-col design-modal-form-group" v-if="beta">
              <fts-toggle-switch
                :form-data="formData"
                :item="{ fieldName: 'timeModifier' }"
                mat-class
                :size="15"
                class="design-modal-toggle-switch"
              >
                <div slot="label" class="design-modal-toggle-switch__label">
                  Timing&nbsp;
                  <fts-release-badge class="fts-float-right" />
                </div>
              </fts-toggle-switch>
              <fts-transition-expand>
                <div
                  class="design-modal-card design-modal-card-mt"
                  v-if="formData.timeModifier"
                >
                  <div class="fts-row gutters">
                    <!-- before (target date) -->
                    <div
                      class="fts-col fts-col-md-4"
                      v-if="formData.trigger === 'target'"
                    >
                      <fts-form-element
                        :form-data="formData"
                        :item="{
                          fieldName: 'before',
                          input: 'toggleSwitch',
                        }"
                        comfort-design
                      />
                    </div>
                    <!-- target time -->
                    <div class="fts-col fts-col-md-4">
                      <fts-form-element
                        :form-data="formData"
                        :item="{
                          fieldName: 'targetTime',
                          type: 6,
                          predefinedFormatId: 't',
                        }"
                        comfort-design
                      />
                    </div>
                    <!-- multipler -->
                    <div class="fts-col fts-col-md-4">
                      <fts-form-element
                        :form-data="formData"
                        :item="{
                          fieldName: 'multipler',
                          type: 3,
                          decimalPlaces: 0,
                        }"
                        comfort-design
                      />
                    </div>
                    <!-- duration type -->
                    <div class="fts-col fts-col-md-4">
                      <fts-form-element
                        :form-data="formData"
                        :item="{
                          fieldName: 'durationType',
                          icon: 'fts-icon-menu-caret-down',
                        }"
                        comfort-design
                      >
                        <fts-select
                          slot="formElement"
                          slot-scope="{ formData, item }"
                          :formData="formData"
                          :item="item"
                          :options="dateTimeOptions"
                          comfort-design
                        />
                      </fts-form-element>
                    </div>
                  </div>
                </div>
              </fts-transition-expand>
            </div>
            <!-- recurring -->
            <div class="fts-col design-modal-form-group" v-if="beta">
              <fts-toggle-switch
                :form-data="formData"
                :item="{ fieldName: 'repeat' }"
                mat-class
                :size="15"
                class="design-modal-toggle-switch"
              >
                <div slot="label" class="design-modal-toggle-switch__label">
                  Recurring&nbsp;
                  <fts-release-badge class="fts-float-right" />
                </div>
              </fts-toggle-switch>
              <fts-transition-expand>
                <div
                  class="design-modal-card design-modal-card-mt"
                  v-if="formData.repeat"
                >
                  <div class="fts-row gutters">
                    <!-- starts on -->
                    <div class="fts-col fts-col-md-4">
                      <fts-form-element
                        :form-data="formData"
                        :item="{
                          fieldName: 'startsOn',
                          type: 6,
                        }"
                        comfort-design
                      />
                    </div>
                    <!-- every -->
                    <div class="fts-col fts-col-md-4">
                      <fts-form-element
                        :form-data="formData"
                        :item="{
                          fieldName: 'every',
                          type: 3,
                        }"
                        comfort-design
                      />
                    </div>
                    <!-- recurring type -->
                    <div class="fts-col fts-col-md-4">
                      <fts-form-element
                        :form-data="formData"
                        :item="{
                          fieldName: 'recurringType',
                          icon: 'fts-icon-menu-caret-down',
                        }"
                        comfort-design
                      >
                        <fts-select
                          slot="formElement"
                          slot-scope="{ formData, item }"
                          :formData="formData"
                          :item="item"
                          :options="dateTimeOptions"
                          comfort-design
                        />
                      </fts-form-element>
                    </div>
                    <!-- recurring week -->
                    <div
                      class="fts-col"
                      v-if="formData.recurringType === 'week'"
                    >
                      <fts-form-element
                        :form-data="formData"
                        :item="{
                          fieldName: 'recurringWeeks',
                          icon: 'fts-icon-check-square',
                        }"
                        comfort-design
                      >
                        <fts-toggle-tokens
                          slot="formElement"
                          slot-scope="{ formData, item }"
                          v-model="formData[item.fieldName]"
                          :options="weeks"
                          multiple
                          comfort-design
                        />
                      </fts-form-element>
                    </div>
                    <!-- recurring month -->
                    <div
                      class="fts-col"
                      v-if="formData.recurringType === 'month'"
                    >
                      <div class="design-modal-form-group">
                        <div class="design-modal-form-label">Recurring:</div>
                        <div class="event-checklist comfort-form-design">
                          <div class="event-checklist-item">
                            <checkbox
                              id="recurringMonthStart"
                              v-model="formData.recurringMonthStart"
                              color="var(--fts-primary)"
                              :size="18"
                              class="event-checkbox"
                            />
                            <label
                              for="recurringMonthStart"
                              class="event-checklist-item-label"
                              >Start of the month</label
                            >
                          </div>

                          <div class="event-checklist-item">
                            <checkbox
                              id="recurringMonthDay"
                              v-model="recurringMonthDay"
                              color="var(--fts-primary)"
                              :size="18"
                              class="event-checkbox"
                            />
                            <label
                              for="recurringMonthDay"
                              class="event-checklist-item-label"
                              >Every</label
                            >
                            <span class="fts-inline-spacer-5"></span>
                            <fts-integer
                              v-model="formData.recurringMonthDayEvery"
                              :max="31"
                              :min="1"
                              inline
                              :disabled="!recurringMonthDay"
                              class="event-checklist-integer"
                              ref="eventChecklistInteger"
                            />
                            <span class="fts-inline-spacer-5"></span>
                            <label
                              for="recurringMonthDay"
                              class="event-checklist-item-label"
                              >of the month</label
                            >
                          </div>

                          <div class="event-checklist-item">
                            <checkbox
                              id="recurringMonthEnd"
                              v-model="formData.recurringMonthEnd"
                              color="var(--fts-primary)"
                              :size="18"
                              class="event-checkbox"
                            />
                            <label
                              for="recurringMonthEnd"
                              class="event-checklist-item-label"
                              >End of the month</label
                            >
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </fts-transition-expand>
            </div>
          </div>
        </fts-collapsible-element>
      </template>
      <!-- footer:left -->
      <template #footerLeft>
        <fts-tippy content="Delete" v-if="formData.id">
          <fts-btn slot="trigger" text icon @click="remove">
            <font-awesome-icon :icon="['far', 'trash-alt']" />
          </fts-btn>
        </fts-tippy>

        <fts-tippy content="Duplicate" v-if="formData.id && beta">
          <fts-btn slot="trigger" text @click="duplicate">
            <font-awesome-icon :icon="['far', 'clone']" />
            <fts-release-badge />
          </fts-btn>
        </fts-tippy>
      </template>
      <!-- footer:right -->
      <template #footerRight>
        <fts-btn text @click="beforeClose">Cancel</fts-btn>

        <fts-btn outline color="primary" @click="done">
          <font-awesome-icon :icon="['fas', 'check']" />Ok
        </fts-btn>
      </template>
    </fts-design-modal>
    <!-- attachment modal -->
    <fts-design-modal
      :title="`Attachment for ${title}`"
      desc="Notification email attachment designer"
      @closed="closeAttachmentModal"
      ref="attachmentModal"
    >
      <div slot="body" class="compact-form-design">
        <label class="fts-form-label label-top">Listing</label>
        <div class="fts-form-group">
          <fts-select
            :formData="attachmentFormData.configuration"
            :item="{ fieldName: 'menu' }"
            :options="menus"
          >
            <fts-label-group
              slot="selected-option-container"
              slot-scope="{ option }"
              :item-id="option.id"
              :list="menus"
              emoji
            />
            <fts-label-group
              slot="option"
              slot-scope="{ option }"
              :item-id="option.id"
              :list="menus"
              emoji
            />
          </fts-select>
        </div>

        <label class="fts-form-label label-top">Profile Only</label>
        <div class="fts-form-group">
          <fts-toggle-switch
            :formData="attachmentFormData.configuration"
            :item="{ fieldName: 'profileOnly' }"
            :size="18"
            :label="false"
            matClass
          />
        </div>

        <template
          v-if="
            attachmentFormData.configuration &&
            !attachmentFormData.configuration.profileOnly
          "
        >
          <label class="fts-form-label label-top">Query Filter</label>
          <div class="fts-form-group">
            <fts-query-designer
              :filter="attachmentFormData.configuration.filter"
              :states="states"
              :informationSource="informationSource"
            />
          </div>

          <div class="fts-form-group">
            <fts-script-editor
              editor-id="queryScript"
              v-model="attachmentFormData.configuration.queryScript"
              lang="python"
            >
              <label slot="label" class="fts-form-label label-top">
                Query Script
              </label>
            </fts-script-editor>
          </div>
        </template>
      </div>
      <template #footer>
        <fts-btn
          outline
          color="primary"
          @click="updateAttachmentFormData(attachmentFormData)"
        >
          Done
        </fts-btn>
      </template>
    </fts-design-modal>
  </div>
</template>

<script>
import Vue from "vue";
import changesets from "diff-json";

import commons from "../commons/commons";
import queryLogic from "../commons/queryLogic";
import { authorizationStore } from '../store/authorizationStore';

import FTSDesignModal from "./FTSDesignModal";
import FTSScriptEditor from "./FTSScriptEditor";
import FTSTemplateEditor from "./FTSTemplateEditor.vue";
import FTSAutomationDesigner from "./automation/FTSAutomationDesigner";
import FTSQueryDesigner from "./FTSQueryDesigner";
import FTSBtn from "../elements/FTSBtn";
import FTSCollapsibleElement from "../elements/FTSCollapsibleElement";
import FTSContenteditable from "../elements/FTSContenteditable";
import FTSDropdown from "../elements/FTSDropdown";
import FTSDropdownSelect from "../elements/FTSDropdownSelect";
import FTSFormElement from "../elements/FTSFormElement";
import FTSMoji from "../elements/FTSMoji";
import FTSProfile from "../elements/FTSProfile";
import FTSReleaseBadge from "../elements/FTSReleaseBadge";
import FTSSelect from "../elements/FTSSelect";
import FTSState from "../elements/FTSState";
import FTSLabelGroup from "../elements/display/FTSLabelGroup";
import FTSToggleSwitch from "../elements/FTSToggleSwitch";
import FTSTransitionExpand from "../elements/FTSTransitionExpand";
import FTSTippy from "../elements/FTSTippy";
import FTSToggleTokens from "../elements/FTSToggleTokens";
import FTSInteger from "../elements/FTSInteger";
import FTSFormula from "../../components/design/FTSFormula.vue";

import Checkbox from "vue-material-checkbox";

export default {
  components: {
    "fts-design-modal": FTSDesignModal,
    "fts-script-editor": FTSScriptEditor,
    "fts-template-editor": FTSTemplateEditor,
    "fts-automation-designer": FTSAutomationDesigner,
    "fts-query-designer": FTSQueryDesigner,
    "fts-btn": FTSBtn,
    "fts-collapsible-element": FTSCollapsibleElement,
    "fts-contenteditable": FTSContenteditable,
    "fts-dropdown": FTSDropdown,
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-form-element": FTSFormElement,
    "fts-moji": FTSMoji,
    "fts-profile": FTSProfile,
    "fts-release-badge": FTSReleaseBadge,
    "fts-select": FTSSelect,
    "fts-state": FTSState,
    "fts-label-group": FTSLabelGroup,
    "fts-toggle-switch": FTSToggleSwitch,
    "fts-transition-expand": FTSTransitionExpand,
    "fts-tippy": FTSTippy,
    "fts-toggle-tokens": FTSToggleTokens,
    "fts-integer": FTSInteger,
    "fts-formula": FTSFormula,
    checkbox: Checkbox,
  },
  data: () => ({
    websiteURL: commons.websiteURL,
    communityURL: commons.communityURL,
    automationType: null,
    formData: {},
    originalData: null,
    resolve: null,
    advanced: false,
    previewLoading: false,
    previewSending: false,
    notificationPreview: null,
    actionTypes: [
      { id: "notification", name: "Notification" },
      { id: "automation", name: "Automation" },
      { id: "advanced", name: "Advanced" },
    ],
    triggers: [
      { id: "change", name: "changed" },
      { id: "target", name: "due" },
      { id: "execute", name: "performing" },
      { id: "changeFrom", name: "changed from" },
      { id: "changeTo", name: "changed to" },
    ],
    dateTimeOptions: [
      { id: "day", name: "Day" },
      { id: "week", name: "Week" },
      { id: "month", name: "Month" },
    ],
    weeks: [
      { name: "Monday", value: "monday" },
      { name: "Tuesday", value: "tuesday" },
      { name: "Wednesday", value: "wednesday" },
      { name: "Thursday", value: "thursday" },
      { name: "Friday", value: "friday" },
      { name: "Saturday", value: "saturday" },
      { name: "Sunday", value: "sunday" },
    ],
    recipientOptions: [],
    attachmentFormData: {},
  }),
  computed: {
    variables() {
      return [
        {
          name: "Entry",
          fieldName: "entry",
          type: 35,
          moduleId: this.$store.state.vectorId.toString(),
        },
      ];
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    beta() {
      return authorizationStore.getters.isBeta;
    },
    workspaceModules() {
      return this.$store.getters.workspaceModules;
    },
    vectorId() {
      return this.$store.state.vectorId;
    },
    moduleId() {
      return this.$store.state.vectorId.toString();
    },
    information() {
      return this.$store.state.canvas.information;
    },
    standardActivities() {
      return this.$store.state.canvas.standardActivities;
    },
    activities() {
      return this.$store.state.canvas.activities;
    },
    states() {
      return this.$store.state.canvas.states;
    },
    entryTemplates() {
      if (
        this.$store.state.canvas.information &&
        this.$store.state.canvas.documents
      ) {
        return [
          ...this.$store.state.canvas.information
            .filter((x) => x.type >= 10 && x.type <= 13)
            .map(function (x) {
              return { id: x.id, name: x.displayName };
            }),
          ...this.$store.state.canvas.documents,
        ];
      }

      return [];
    },
    menus() {
      if (this.$store.state.canvas) return this.$store.state.canvas.menus || [];
      return [];
    },
    informationSource() {
      return queryLogic.querySelectionPrefix
        .concat(this.information)
        .concat(queryLogic.querySelectionSuffix);
    },
    getInformation() {
      return this.information.find((x) => x.id === this.formData.item);
    },
    getState() {
      return this.states.find((x) => x.id === this.formData.item);
    },
    getStandardActivity() {
      return this.standardActivities.find((x) => x.id === this.formData.item);
    },
    getActivity() {
      return this.activities.find((x) => x.id === this.formData.item);
    },
    title() {
      if (!this.formData.item) return this.formData.type;

      try {
        switch (this.formData.type) {
          case "state":
            return this.getState && this.getState.name;
          case "activity":
            return this.getActivity && this.getActivity.name;
          case "standardActivity":
            return this.getStandardActivity && this.getStandardActivity.name;
          case "information":
            return this.getInformation && this.getInformation.displayName;
        }
      } catch (err) {
        console.error("AutomationDesign:Title");
      }
    },
    channels() {
      let channels = [
        { id: "fetias", name: "App Notification", value: "fetias", icon: ['far', 'bell'] },
        { id: "email", name: "Email", value: "email", icon: ['far', 'envelope'] },
      ]

      if (this.beta)
        channels = channels.concat([
          { id: "sms", name: "SMS", value: "sms", beta: true },
        ]);

      if (this.isInternal)
        channels = channels.concat([
          { id: "slack", name: "Slack", value: "slack", internal: true },
          { id: "whatsapp", name: "Whatsapp", value: "whatsapp", internal: true },
          { id: "cloudMessaging", name: "Cloud Message (FCM)", value:"cloudMessaging", internal: true },
        ]);
      
      return channels;
    },
    eventType() {
      try {
        switch (this.formData.type) {
          case "vector":
            return "vector";
          case "activities":
            return "activityConsole";
          case "standardActivities":
            return "standardConsole";
          case "states":
            return "stateConsole";
          case "state":
            return "state";
          case "activity":
            return "activity";
          case "standardActivity":
            return "standardActivity";
          case "information":
            if (!this.formData.item) return "informationConsole";
            const informationType =
              this.getInformation && this.getInformation.type;
            return informationType &&
              (informationType == 5 || informationType == 6)
              ? "due"
              : "information";
        }
      } catch (err) {
        console.error("AutomationDesign:EventType", err);
      }
    },
    eventBadges() {
      let badges = [];
      if (this.formData.general)
        badges.push({
          name: "Execute Once",
          description:
            "execute exactly one time for each trigger without iterate through entries",
        });
      if (this.formData.condition)
        badges.push({
          name: "Condition",
          description: "only trigger when condition is met",
        });
      if (this.formData.timeModifier)
        badges.push({
          name: "Timing",
          description: "only trigger when the timing is met",
        });
      if (this.formData.repeat)
        badges.push({
          name: "Recurring",
          description: "trigger repeatedly when the recurring condition is met",
        });
      if (badges.length) return badges;
    },
    getAutomation() {
      /* vector */
      if (this.formData.type === "vector")
        return this.$store.getters.vectorTriggers.filter(
          (x) => x.type === "vector"
        );
      /* console element */
      if (this.formData.type && !this.formData.item)
        return this.$store.getters.vectorTriggers.filter(
          (x) => x.type === this.formData.type && !x.item
        );
      /* default */
      return this.$store.getters.vectorTriggers.filter(
        (x) => x.item === this.formData.item
      );
    },
    recurringMonthDay: {
      get() {
        return Boolean(this.formData.recurringMonthDay);
      },
      set(val) {
        this.$set(this.formData, "recurringMonthDay", val);
        if (val) {
          this.$nextTick(() => {
            this.$refs.eventChecklistInteger.$el
              .getElementsByTagName("input")[0]
              .focus();
          });
        } else if (!val && this.formData.recurringMonthDayEvery) {
          this.$delete(this.formData, "recurringMonthDayEvery");
        }
      },
    },
    actionTypeAdvanced: {
      get() {
        return this.formData.actionType === 'advanced';
      },
      set(val) {
        if (val) {
          this.$set(this.formData, 'actionType', 'advanced');
        } else {
          this.$set(this.formData, 'actionType', 'automation');
          this.selectActionType('automation');
        }
      }
    },
  },
  methods: {
    async show(automation) {
      /* get automation */
      if (automation.id) {
        const findAutomation = this.$store.getters.vectorTriggers.find(
          (x) => x.id === automation.id
        );
        if (findAutomation) {
          this.formData = _.cloneDeep(findAutomation);
        }
      } else {
        this.formData = automation;
      }
      /* enabled */
      if (
        this.formData.enabled === null ||
        this.formData.enabled === undefined
      ) {
        this.$set(this.formData, "enabled", true);
      }
      /* initial:action type */
      if (!this.formData.id && !this.formData.actionType) {
        this.$set(this.formData, "actionType", this.actionTypes[0].id);
      } else if (
        this.formData.id && 
        !this.formData.actionType && 
        Boolean(this.formData.channel)
      ) {
        this.$set(this.formData, "actionType", this.actionTypes[0].id);
      }
      this.selectActionType(this.formData.actionType);

      return new Promise((resolve) => {
        this.resolve = resolve;
        this.$refs.ftsDesignModal.show({
          beforeCloseHandler: () => {
            const diff = changesets.diff(this.formData, this.originalData);
            if (diff && diff.length) {
              this.$modal.show("dialog", {
                title: "Confirm discard changes",
                text: "Discard all changes?",
                buttons: [
                  { title: "Cancel" },
                  {
                    title: "Discard",
                    default: true,
                    handler: () => {
                      this.$modal.hide("dialog");
                      this.reset();
                    },
                  },
                ],
              });
            } else {
              this.reset();
            }
          },
        });

        this.$nextTick(() => {
          this.originalData = _.cloneDeep(this.formData);
        });
      });
    },
    done() {
      this.$store.dispatch("setVectorTrigger", this.formData);
      this.resolve && this.resolve(this.formData);
      this.reset();
    },
    beforeClose() {
      this.$refs.ftsDesignModal.beforeClose();
    },
    remove() {
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: "Delete automation?",
        buttons: [
          { title: "Cancel" },
          {
            title: "Delete",
            default: true,
            handler: () => {
              this.$modal.hide("dialog");
              this.$store.dispatch("removeVectorTrigger", this.formData.id);
              this.resolve && this.resolve(true);
              this.reset();
            },
          },
        ],
      });
    },
    reset() {
      this.$refs.ftsDesignModal.close();
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    afterShow() {
      switch (this.formData.type) {
        case "state":
          this.triggers = [
            { id: "changeTo", name: "changed to" },
            { id: "changeFrom", name: "changed from" },
          ];

          if (this.formData.trigger) break;

        case "activity":
          this.triggers = [{ id: "execute", name: "performing" }];
          break;

        case "standardActivity":
          this.triggers = [{ id: "execute", name: "performing" }];
          break;

        case "information":
          this.triggers = [{ id: "change", name: "changed" }];

          if (this.formData.item) {
            let informationItem = this.information.find(
              (x) => x.id == this.formData.item
            );
            if (
              informationItem != null &&
              (informationItem.type == 5 || informationItem.type == 6)
            ) {
              this.triggers = [
                { id: "change", name: "changed" },
                { id: "target", name: "due" },
              ];
            }
          }
          break;
      }
      if (!this.triggers.some((x) => x.id == this.formData.trigger)) {
        this.formData.trigger = this.triggers[0].id;
      }
    },
    duplicate() {
      let cloned = _.cloneDeep(this.formData);
      delete cloned.id;
      if (cloned.description) {
        cloned.description = `${cloned.description} (Copy)`;
      } else {
        const actionType = this.actionTypes.find(
          (x) => x.id === cloned.actionType
        );
        if (actionType && actionType.name)
          cloned.description = `${actionType.name} (Copy)`;
        else cloned.description = `Automation (Copy)`;
      }

      function setDescription(automation, i) {
        const description = `${cloned.description} (${i})`;
        if (automation.some((x) => x.description === description))
          return setDescription(automation, i + 1);
        else return description;
      }

      try {
        const automation = this.getAutomation.filter(
          (x) => x.description === cloned.description
        );
        if (automation.length)
          cloned.description = setDescription(
            this.getAutomation,
            automation.length
          );
      } catch (er) {
        console.error("AutomationDesign:Duplicate", err);
      }

      this.$store.dispatch("setVectorTrigger", cloned);
      this.beforeClose();
    },
    informationIcon(id) {
      return commons.informationIcon(id) || "fts-icon-text";
    },
    getModuleEmoji(id) {
      const res = this.workspaceModules.find((x) => x.id == id);
      return res && res.emoji;
    },
    getModuleName(id, name) {
      const res = this.workspaceModules.find((x) => x.id == id);
      return res && res.name ? res.name : name || "(undefined)";
    },
    /* recipients */
    async loadRecipients(search) {
      search = search || "";
      let vm = this;
      let result = await vm.$store.dispatch("searchRecipient", {
        search: search,
        workspaceId: vm.$store.state.vector.ownerWorkspaceId,
      });

      var types = [20, 0, 16, 25, 26];

      let information = vm.$store.state.canvas.information
        .filter((x) => {
          return (
            types.some((y) => y == x.type) &&
            x.displayName.toLowerCase().indexOf(search.toLowerCase()) > -1
          );
        })
        .map((x) => {
          return {
            id: x.id,
            recipientType: 5,
            informationType: x.type,
            name: x.displayName,
          };
        });

      information = information.concat([
        {
          id: "#createdBy",
          type: 20,
          name: "Created By",
        },
        {
          id: "#updatedBy",
          type: 20,
          name: "Last Updated By",
        },
        {
          id: "#assignees",
          type: 6,
          name: "Assignees",
        },
      ]);

      vm.recipientOptions = _.orderBy(
        result.data.concat(information),
        ["type", (x) => x.name && x.name.toLowerCase()],
        ["desc", "asc"]
      );
    },
    async preloadRecipients(loading) {
      loading(true);
      await this.loadRecipients();
      loading(false);
    },
    searchRecipients: _.debounce(async (loading, search, vm) => {
      await vm.loadRecipients(search);
      loading(false);
    }, 300),
    fetchRecipients(event) {
      event.loading(true);
      this.searchRecipients(event.loading, event.searchText, this);
    },
    /* action type */
    selectActionType(item) {
      if (item === "notification")
        this.$set(this.formData, "notification", true);
      else
        this.$delete(this.formData, "notification");
      /* initial:set automation type */
      if (item === "automation" && !this.automationType) {
        if (this.formData.automationBefore)
          this.automationType = "before";
        else if (this.formData.automation)
          this.automationType = "after";
      }
    },
    /* attachment */
    addAttachment() {
      let obj = {
        id: commons.nanoID(),
      };
      if (Array.isArray(this.formData.attachments))
        this.formData.attachments.push(obj);
      else this.$set(this.formData, "attachments", [obj]);
    },
    removeAttachment(data) {
      let tableData = this.formData.attachments;
      tableData.splice(tableData.indexOf(data), 1);
    },
    openAttachmentModal(data) {
      if (data.configuration == null) {
        data.configuration = {
          filter: { items: [] },
        };
      }

      this.attachmentFormData = _.cloneDeep(data);
      this.$refs.attachmentModal.show();
    },
    updateAttachmentFormData(data) {
      let attachment = this.formData.attachments.find(
        (x) => x.id === this.attachmentFormData.id
      );
      Object.assign(attachment, data);
      this.$refs.attachmentModal.close();
    },
    closeAttachmentModal() {
      this.attachmentFormData = {};
    },
    /* test automation */
    async testAutomation() {
      this.previewSending = true;
      try {
        await this.$store.dispatch("testAutomation", this.formData);
      } catch (err) {
        this.$toasted.global.error({
          response: err.response,
        });
        console.error("AutomationDesign:TestAutomation", err);
      } finally {
        this.previewSending = false;
      }
    },
    /* preview */
    async preview() {
      this.previewLoading = true;
      try {
        let res = await this.$store.dispatch(
          "previewAutomation",
          this.formData
        );
        this.notificationPreview = res.data;
        /* recipients */
        if (this.notificationPreview && this.notificationPreview.recipients) {
          this.notificationPreview.recipients = [...new Set(
            this.notificationPreview.recipients.map(x => {
              const res = this.$store.state.users.find(y => y.profileId == x);
              return res && res.username ? res.username : x;
            })
          )];
        }
        /* content */
        this.$nextTick(() => {
          try {
            let iframe = this.$refs.notificationContentIframe;
            iframe.height = "auto";
            iframe.contentWindow.document.body.style.margin= 0;
            iframe.contentWindow.document.body.style.fontFamily = "'Roboto', sans-serif";
            iframe.contentWindow.document.body.innerHTML = this.notificationPreview.content;
            iframe.height = iframe.contentWindow.document.body.offsetHeight;
          } catch(err) {
            console.warn("[DOMException]", err);
          }
        });
      } catch (err) {
        this.$toasted.global.error({
          response: err.response,
        });
        console.error("AutomationDesign:Preview", err);
      } finally {
        this.previewLoading = false;
      }
    },
    /* setup automation */
    setupAutomation(automationType) {
      if (!this.formData[automationType]) {
        Vue.set(this.formData, automationType, {
          testLocusId: null,
          testLocus: null,
          testLocusDetailId: null,
          testLocusDetail: null,
        });
        this.formData[automationType].blocks = {
          1: {
            id: 1,
            name: "start",
            data: {
              id: "start",
              variables: [
                {
                  id: "form",
                  fieldName: "form",
                  name: "Form",
                  activityId: null,
                  nodeId: 1,
                },
                {
                  id: "entry",
                  fieldName: "entry",
                  name: "Entry",
                  nodeId: 1,
                },
              ],
            },
            class: "start",
            html: "start",
            typenode: "vue",
            outputs: {
              output_1: {
                connections: [],
              },
            },
            inputs: {},
            pos_x: 200,
            pos_y: 50,
          },
        };

        if (
          this.formData.type == "information" ||
          this.formData.type == "state"
        ) {
          this.formData[automationType].blocks[1].data.variables.splice(0, 1);
        } else {
        }
      }

      if (this.formData[automationType].blocks[1].data.variables.length) {
        let form = this.formData[automationType].blocks[1].data.variables.find(
          (x) => x.id == "form"
        );
        if (form) {
          form.type = 35;
          form.moduleId = this.vectorId.toString();
          form.activityId = this.formData.item;
        }

        let entry = this.formData[automationType].blocks[1].data.variables.find(
          (x) => x.id == "entry"
        );
        if (entry) {
          entry.type = 7;
          entry.moduleId = this.vectorId.toString();
        }
      }
    },
  },
  watch: {
    "formData.actionType": function (val) {
      if (val == "automation") {
        if (this.automationType == "before") {
          this.setupAutomation("automationBefore");
        } else if (this.automationType == "after") {
          this.setupAutomation("automation");
        }
      }
    },
    automationType: function (val) {
      if (this.automationType == "before") {
        this.setupAutomation("automationBefore");
      } else if (this.automationType == "after") {
        this.setupAutomation("automation");
      }
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-event-config-modal {
  /* event toggle label */
  .event-toggle-label {
    margin-right: 0.375rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--fts-form-label);
  }
  /* event type */
  .event-type {
    min-width: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    font-weight: 700;
    font-size: 1.5rem;
    line-height: 1.5;
    .event-type-activity {
      min-width: 0;
      display: flex;
      align-items: center;
    }
    .event-type-information {
      min-width: 0;
      display: flex;
      align-items: center;
    }
    .event-type-state {
      > div {
        padding: 0.375rem 0.75rem;
        font-size: 1rem;
      }
    }
    .event-type-emoji {
      margin-right: 0.375rem;
    }
    .event-type-icon {
      margin-right: 0.375rem;
      color: $secondaryColor;
    }
    .event-type-label-ellipsis {
      @include text-overflow-ellipsis;
    }
    .event-type-select {
      display: inline-flex;
      &.event-type-select--bordered {
        .event-type-select__toggle {
          margin-top: 0.25rem;
          margin-bottom: 0.25rem;
          padding: 0.125rem 0.5rem;
          border: 1px solid var(--fts-gray-3);
          border-radius: 0.25rem;
        }
      }
      &:not(.event-type-select--bordered) {
        border-bottom: 1px solid var(--fts-gray-3);
      }
      .event-type-select__toggle {
        .event-type-toggle-icon {
          margin-left: 0.375rem;
          font-size: 0.875rem;
        }
      }
    }
  }
  /* event badges */
  .event-badges {
    @include flex-gap($display: flex, $gap: 0.375rem);
    padding-top: 0.275rem;
  }
  /* event channel */
  .event-channel-container {
    margin-top: .325rem;
    margin-bottom: .325rem;
    .event-channel-icon {
      margin-right: .25rem;
    }
  }
  /* event test action */
  .event-test {
    .event-test-legend {
      margin-bottom: 1.5rem;
      font-size: .875rem;
      color: var(--fts-form-label);
    }
    padding: 1rem;
    border: 1px solid var(--fts-gray-3);
    border-radius: 0.25rem;
    .event-test-selector {
      .fts-form-group {
        margin-bottom: 1rem;
      }
    }
    .event-test-action {
      display: flex;
      align-items: center;
      > *:not(:first-child) {
        margin-left: 0.5rem;
      }
    }
    .event-test-action-helper {
      margin-top: .5rem;
      font-size: 0.75rem;
      color: var(--fts-form-helper);
      line-height: 1rem;
    }
  }
  /* event attachment */
  .event-attachment {
    display: flex;
    align-items: center;
    padding-bottom: 0.75rem;
    .event-attachment-selection {
      flex: 1;
      margin-right: 0.75rem;
    }
  }
  /* event preview */
  .event-preview {
    margin-top: 2rem;
    padding-bottom: 1rem;
    .event-preview-wrapper {
      &:not(:last-child) {
        margin-bottom: 1.5rem;
      }
    }
    .event-preview-iframe {
      width: 100%;
      border: 0;
    }
    .event-preview-subject-wrapper {
      .event-preview-subject-badge {
        margin-right: .125rem;
        border-radius: .25rem;
        vertical-align: text-bottom;
      }
      .event-preview-subject {
        font-weight: 500;
        font-size: 1.125rem;
        line-height: 1.75rem;
      } 
    }
    .event-preview-recipient-container {
      @include flex-gap($display: flex, $gap: .5rem);
      align-items: center;
      margin-top: .025rem;
      font-size: .875rem;
      line-height: 1.25rem;
      .event-preview-recipient {
        @include text-overflow-ellipsis;
        padding: .25rem .375rem;
        border-radius: 10em;
        background-color: var(--fts-gray-2);
      }
      .event-preview-recipient-tippy {
        min-width: 0;
      }
    }
    .event-preview-content {
      padding-top: 1.5rem;
      border-top: 1px solid var(--fts-gray-3);
    }
  }
  /* event automation selection */
  .event-automation-selection {
    margin-bottom: 1rem;
    .automation-design-toggle {
      @include user-select-none;
      display: inline-flex;
      align-items: center;
      padding: 0.135rem 0.75rem;
      border-bottom: 2px solid transparent;
      background: var(--fts-gray-1);
      font-size: 0.875rem;
      font-weight: 500;
      line-height: 2.5;
      cursor: pointer;
      &:not(.automation-design-toggle--active) {
        &:hover {
          @include theme() {
            background: rgba($color: theme-get("fts-gray-4"), $alpha: 0.5);
          }
        }
      }
      &.automation-design-toggle--active {
        border-bottom-color: $primaryColor;
        background: rgba($color: $primaryColor, $alpha: 0.095);
        color: $primaryColor;
      }
      .automation-design-toggle__action {
        margin-left: 0.375rem;
      }
    }
  }
  /* event checkbox */
  .event-checkbox {
    width: auto;
    margin: 0 1.175rem 0 0.575rem;
    .m-chckbox--label {
      padding: 0;
    }
  }
  /* event checklist */
  .design-modal-form-label + .event-checklist {
    margin-top: 0.75rem;
  }
  .event-checklist {
    .event-checklist-item {
      display: flex;
      align-items: center;
      margin-bottom: 1rem;
      .event-checklist-item-label {
        @include user-select-none;
        cursor: pointer;
      }
    }
    .event-checklist-integer {
      > .fts-form-control {
        width: 60px;
      }
    }
  }
  /* coming soon */
  .event-beta-message {
    padding: 2rem;
    .event-beta-message-title {
      margin-bottom: 1.5rem;
      font-size: 1.5rem;
      font-weight: 700;
    }
    .event-beta-message-description {
      font-size: 0.875rem;
    }
  }
}
/* event type content */
.event-type-content.event-type-dropdown-menu {
  font-size: 1rem;
}
</style>